import {
  Component,
  Inject,
  OnInit,
  OnDestroy,
  ViewEncapsulation,
  Renderer2,
  PLATFORM_ID } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Router, ActivatedRoute, ParamMap } from '@angular/router';
import { Angulartics2GoogleAnalytics } from 'angulartics2';
import { Subscription, Subject } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { OAuthService } from 'angular-oauth2-oidc';
import { Auth } from '@app/models';
import { FavouritesService } from '@app/modules/user/services/favourites.service';
import { ClickService } from '@app/services/click.service';
import { ApplyClickService } from '@app/services/apply-click.service';
import { environment } from '@env/environment';
import { WebpService } from '@app/services';
import { MjsJob } from '@app/models/mjs-job.model';
import { MjsJobMetricsService } from '@app/services/mjs-job-metrics.service';

declare var window: any;
declare var lumesseAnalytics: any;

@Component({
  selector: 'mjs-mjs-job',
  templateUrl: './mjs-job.component.html',
  styleUrls: ['./mjs-job.component.scss'],
  providers: [FavouritesService, WebpService, MjsJobMetricsService],
  encapsulation: ViewEncapsulation.None
})
export class MJSJobComponent implements OnInit, OnDestroy {

  currentAuth: Auth;
  currentAuthSubscription: Subscription;

  content: MjsJob;
  map: boolean;
  applyButton: any;
  isBrowser: boolean;
  featured: boolean;
  partnership: boolean;
  emailOnlyAccount: boolean;
  mobile: boolean;
  sType: string;
  cType: string;
  locationsOpen: boolean;

  private onDestroy$: Subject<void> = new Subject<void>();

  constructor(@Inject(PLATFORM_ID) private platform: any,
    @Inject(DOCUMENT) private doc: Document,
    private renderer: Renderer2,
    private analytics: Angulartics2GoogleAnalytics,
    private oauthService: OAuthService,
    private toast: ToastrService,
    private favourites: FavouritesService,
    private router: Router,
    private route: ActivatedRoute,
    private metricsService: MjsJobMetricsService,
    public webp: WebpService) {
  }

  ngOnInit(): any {
    this.route.data.subscribe(res => {
      this.content = res['content'];

      if (isPlatformBrowser(this.platform) && this.oauthService.hasValidAccessToken()) {
        this.favourites.get(this.content.id).subscribe((res: any[]) => {
          if (res.length > 0) {
            this.content.user_like = true;
          }
        })
      }

      if (!this.content) {
        this.router.navigate(['/404']);
      }
    });

    this.renderer.addClass(this.doc.body, 'single-job');

    if (isPlatformBrowser(this.platform)) {
      this.isBrowser = true;
    }

    this.route.queryParamMap
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((paramMap: ParamMap) => {
        const jobAction = paramMap.get('job_action');
        this.featured = paramMap.get('featured') ? true : false;
        this.partnership = paramMap.get('partnership') ? true : false;
        this.emailOnlyAccount = paramMap.get('eoa') ? true : false;
        this.sType = paramMap.get('sType');
        this.cType = paramMap.get('cType');

        if (!this.sType) {
          this.sType = 'myjobscotland';
        }

        if (this.content) {
          let eventStr = 'Job View';

          if (this.partnership) {
            eventStr = `Partnership ${eventStr}`;
          } else if (this.featured) {
            eventStr = `Featured ${eventStr}`;
          }

          if (isPlatformBrowser(this.platform)) {
            this.analytics.eventTrack(`view`, {
              category: eventStr,
              label: this.content.job_number || this.content.id,
              noninteraction: true
            });

            // GA4 event.
            window.gtag('event', eventStr, {
              value: this.content.job_number || this.content.id
            });

            if (this.emailOnlyAccount) {
              // GA4 event.
              window.gtag('event', 'Email Only Account Job View', {
                event_category: this.content.job_number || this.content.id,
              });

              this.analytics.eventTrack('view', {
                category: 'Email Only Account Job View',
                label: this.content.job_number || this.content.id,
                noninteraction: true
              });
            }

            this.metricsService.click(parseInt(this.content.id, 10), this.featured)
              .pipe(takeUntil(this.onDestroy$)).subscribe();

            if (this.content.source === 'cornerstone') {
              if (typeof lumesseAnalytics != 'undefined') {
                lumesseAnalytics.pushEvent('AdvertViewed', {
                  sourceBoard: this.sType,
                  advertTitle: this.content.title,
                  advertId: `${this.content.talentlink_id}`,
                  atsLanguageCode: 'GB',
                  clientId: environment.lumesse_client_id
                });
              }
            }
          }
        }

        if (jobAction) {
          switch (jobAction) {
            case 'favourite':
              if (isPlatformBrowser(this.platform)) {
                this.favourite(parseInt(this.content.id, 10));
              }
              break;
          }
        }
      });
  }

  locationName(location: any) {

    const locationSet = new Set();

    let name = location.name as string;

    const re = /[0-9a-f]{8}-([0-9a-f]{4}-){3}[0-9a-f]{12}/;
    if (location.postal_code && !name.match(re) && name !== location.postal_code) {
      if (name.includes(',')) {
        name.split(',').forEach((item) => locationSet.add(item.trim()));
      }
      else {
        locationSet.add(name.trim());
      }
    }

    if (location.address_line1) {
      let address_line1 = location.address_line1 as string;
      if (address_line1.includes(',')) {
        address_line1.split(',').forEach((item) => locationSet.add(item.trim()));
      }
      else {
        locationSet.add(address_line1.trim());
      }
    }

    if (location.locality) {
      locationSet.add(location.locality);
    }
    if (location.postal_code) {
      locationSet.add(location.postal_code);
    }

    if ([...locationSet].join(', ').length === 0) {
      return 'Location not available';
    }

    return [...locationSet].join(', ');
  }

  setGeoLocationCoordinates(job: MjsJob): MjsJob {
    const geolocations = [];
    job.locations.forEach((item) => geolocations.push(item.coordinates));
    job.geolocation_coordinates = geolocations.join('___');
    return job;
  }

  toInteger(v) {
    return parseInt(v, 10);
  }

  salaryDisplay = (): string => {
    const {
      compensation_override,
      compensation_minimum,
      compensation_maximum,
      compensation_period,
      compensation_post
    } = this.content;

    if (compensation_override) {
      return compensation_override;
    }

    let output = `£${new Intl.NumberFormat('en-GB', { }).format(parseFloat(compensation_minimum))}`;

    if (compensation_minimum !== compensation_maximum) {
      output += ` - £${new Intl.NumberFormat('en-GB', { }).format(parseFloat(compensation_maximum))} per ${compensation_period}`;
    }

    if (compensation_post) {
      output += ` ${compensation_post}`;
    }

    return output.toLowerCase();
  }

  apply(): any {
    let path = this.content.url.split('/').reverse()[0];

    this.metricsService.apply(parseInt(this.content.id, 10), this.featured)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe();

    this.analytics.eventTrack('click', {
      category: 'Apply',
      label: this.content.job_number || this.content.id,
      noninteraction: true
    });

    // GA4 event.
    window.gtag('event', 'Apply', {
      label: this.content.job_number || this.content.id,
    });

    if (this.emailOnlyAccount) {
      // GA4 event.
      window.gtag('event', 'Apply by Email Only Account', {
        label: this.content.job_number || this.content.id,
      });

      this.analytics.eventTrack('click', {
        category: 'Apply by Email Only Account',
        label: this.content.job_number || this.content.id,
        noninteraction: true
      });
    }
  }

  showMap(): void {
    // GA4 event.
    window.gtag('event', 'Job Map View', {
      event_category: this.content.job_number || this.content.id,
    });

    this.analytics.eventTrack('map_view', {
      category: `Map View`,
      label: this.content.job_number || this.content.id,
      noninteraction: true
    });

    this.map = true;
  }

  favourite(id: number): void {
    if (this.oauthService.hasValidAccessToken()) {
      this.favourites.patch(id).subscribe({
        next: (data: any) => {
          this.content.user_like = !this.content.user_like;

          this.analytics.eventTrack(`${!this.content.user_like ? 'un' : ''}favourite`, {
            category: `${!this.content.user_like ? 'Unf' : 'F'}avourite`,
            label: this.content.job_number || this.content.id,
            noninteraction: true
          });

          // GA4 event.
          window.gtag('event', `${!this.content.user_like ? 'Unf' : 'F'}avourite`, {
            label: this.content.job_number || this.content.id,
          });

          this.toast.success(data.message)
        },
        error: err => {
          this.signInMessage();
          catchError(err);
        }
      });
    } else {
      this.signInMessage();
    }
  }

  signInMessage() {
    this.toast.error(`<a href="/user/login?redirect=${this.content.url}%3fjob_action=favourite">Sign in</a> or <a href="/user/register">create an account</a> to save jobs to your favourites.`);
  }

  downloadFile(file): void {
    this.analytics.eventTrack('download', {
      category: 'Download',
      label: `Job Advert ID: ${this.content.id} Document: ${file.filename}`,
      noninteraction: true
    });

    // GA4 event.
    window.gtag('event', 'Download', {
      label: this.content.job_number || this.content.id,
    });

    window.open(file.url);
  }

  filename(file) {
    const parts = file.filename.split('.');
    parts.pop();
    return parts.join();
  }

  get mapObject(): any {
    return this.content;
  }

  ngOnDestroy(): void {
    this.renderer.removeClass(this.doc.body, 'single-job');
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

}
